import React, {useReducer} from "react";
import * as api from "./services/api";
import {contractsMapper} from "./mapper/contractsMapper";


const defaultUserData = {
    user: {},
    contracts: {},
};

const GlobalStateContext = React.createContext();

const GlobalStateProvider = props => {

    const [userData, updateUserData] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'setAttributes':
                    return {...state, ...action.attributes}
                case 'updateUser':
                    return {
                        ...state,
                        user: action.user
                    }
                case 'updateRequestContract':
                    return {
                        ...state, contracts: {
                            ...state.contracts,
                            requestContract: action.requestContract,
                        },
                    }
                default:
                    throw new Error(`Cannot find action ${action.type} for global state reducer`);
            }
        },
        defaultUserData,
    );

    /**
     * This method fetch the userData or throws an exception if the user is not authenticated.
     *
     * It is used by the authentication Context to check if the user is authenticated: if this function
     * throws an exception while fetching the data the user is not authenticated (because of a 401)
     * otherwise the user is authenticated.
     */
    const loadUserData = async () => {
        const [user, contracts] = await Promise.all([api.User.getCurrentUser(), api.User.getCurrentContracts()]);
        const userData = {
            ...defaultUserData,
            user,
            contracts: contractsMapper(contracts)
        };
        updateUserData({type: "setAttributes", attributes: userData});
        return userData;
    }

    /**
     * Reset the state to the default.
     *
     * It can be useful after the logout to clean everything.
     */
    const resetGlobalState = () => {
        updateUserData({type: "setAttributes", attributes: defaultUserData});
    }

    /**
     * Update the user.
     *
     * It calls the API to update the user and update the user state.
     */
    const updateUser = async (body) => {
        const user = await api.User.updateCurrentUser(body);
        updateUserData({type: 'updateUser', user: user});
    }

    /**
     * Update the contract.
     *
     * It calls the API to update the user and update the user state.
     */
    const updateRequestContract = async (body) => {
        const requestContract = await api.RequestContract.updateRequestContract(body);
        updateUserData({type: "updateRequestContract", requestContract: requestContract});
    }

    /**
     * Update the request contract settings.
     *
     * It calls the API to update the request contract settings
     */
    const updateSettings = async (body) => {
        await updateRequestContract({settings: body})
    }

    /**
     * Update the request contract settings theme.
     *
     * It calls the API to update the request contract settings theme
     */
    const updateTheme = async (body) => {
        await updateRequestContract({settings: {theme: body}})
    }

    /**
     * Update the request contract logo.
     *
     * It calls the API to update the request contract logo
     */
    const updateLogo = async (body) => {
        await updateRequestContract(body)
    }

    const context = {
        userData,
        settings: !!userData.contracts.requestContract ? userData.contracts.requestContract.settings : null,
        accommodationUnitsSettings: !!userData.contracts.requestContract ? userData.contracts.requestContract.accommodationUnitsSettings : [],
        logo: !!userData.contracts.requestContract ? userData.contracts.requestContract.logo : null,
        theme: !!userData.contracts.requestContract ? userData.contracts.requestContract.settings.theme : null,
        loadUserData,
        resetGlobalState,
        updateSettings,
        updateLogo,
        updateUser,
        updateTheme,
    }

    return <GlobalStateContext.Provider value={context}>
        {props.children}
    </GlobalStateContext.Provider>;
}

const useGlobalState = () => {
    return React.useContext(GlobalStateContext);
};

export {useGlobalState};
export default GlobalStateProvider;